import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, MessageCircle, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { siteData } from '../data';
import ContactForm from '../components/ContactForm';

export default function ContactPage() {
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const faqs = [
    {
      question: "Hvor ofte skal jeg få pudset mine vinduer?",
      answer: "Det afhænger af dine omgivelser. Vi anbefaler typisk hver 8. uge for private hjem i byområder, men hvis du bor tæt på vandet eller en trafikeret vej, kan hver 4. uge være nødvendigt for at holde ruderne krystalklare."
    },
    {
      question: "Skal jeg være hjemme, når I pudser?",
      answer: "Nej, det er ikke nødvendigt, hvis vi kun skal pudse udvendigt. Vi skal blot have adgang til haven/grunden. Skal vi pudse indvendigt, aftaler vi et tidspunkt, hvor du er hjemme."
    },
    {
      question: "Pudser I vinduer i regnvejr?",
      answer: "Ja, vi pudser som udgangspunkt altid vinduer – også selvom det regner. Regnvand er i sig selv rent nok til ikke at efterlade striber; det er snavset på ruden, der gør dem beskidte. Vi garanterer et flot resultat uanset vejret."
    },
    {
      question: "Hvordan betaler jeg?",
      answer: "Vi gør det nemt for dig. Du kan betale via faktura, MobilePay eller tilmelde dig Betalingsservice, hvis du har en fast abonnementsaftale."
    }
  ];

  return (
    <main className="pt-20">
      <Helmet>
        <title>Kontakt VHservice | Få et uforpligtende tilbud</title>
        <meta name="description" content="Kontakt os for et uforpligtende tilbud på vinduespudsning. Vi svarer hurtigt på både telefon og mail." />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gray-900 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/8295997d-6311-4ef0-aa52-99e1d725c6e9.jpg" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Kontakt {siteData.name}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Vi sidder klar til at hjælpe dig med spørgsmål, tilbudsgivning eller tidsbestilling. Fang os på telefonen eller send en besked.
          </motion.p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-12 mt-8 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 shadow-xl border-t-4 border-black text-center flex flex-col items-center"
            >
              <Phone className="w-10 h-10 mb-4 text-blue-600" />
              <h3 className="text-xl font-bold mb-2">Ring direkte</h3>
              <p className="text-gray-600 mb-4">Hurtigst svar på dine spørgsmål.</p>
              <a href={`tel:${siteData.contact.phone}`} className="text-xl font-bold hover:text-blue-600 transition-colors">
                {siteData.contact.phone}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content: Form and FAQ */}
      <div className="bg-white pb-24">
        <ContactForm />

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">Ofte stillede spørgsmål</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Her finder du svar på nogle af de mest almindelige spørgsmål, vi modtager fra vores kunder.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <AnimatePresence mode="popLayout">
              {faqs.slice(0, showAllFaqs ? faqs.length : 2).map((faq, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-8 border-l-4 border-black h-full"
                >
                  <div className="flex items-start space-x-4">
                    <HelpCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-bold mb-3">{faq.question}</h4>
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="mt-12 text-center">
            <button 
              onClick={() => setShowAllFaqs(!showAllFaqs)}
              className="inline-flex items-center space-x-2 text-black font-bold hover:underline transition-all group"
            >
              <span>{showAllFaqs ? 'Vis færre spørgsmål' : 'Vis flere spørgsmål'}</span>
              {showAllFaqs ? (
                <ChevronUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
              ) : (
                <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
              )}
            </button>
          </div>

          <div className="mt-16 bg-blue-600 p-10 text-white flex flex-col md:flex-row items-center justify-between">
            <div className="mb-8 md:mb-0">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">Fandt du ikke svar på dit spørgsmål?</h3>
              <p className="text-blue-100 text-lg">Vores kundeservice sidder klar til at hjælpe dig personligt.</p>
            </div>
            <a 
              href={`tel:${siteData.contact.phone}`}
              className="bg-white text-blue-600 px-10 py-4 font-bold text-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
            >
              Ring nu
            </a>
          </div>
        </section>
      </div>

      {/* Map Section */}
      <section className="h-[450px] relative grayscale hover:grayscale-0 transition-all duration-700 border-t border-gray-100">
        <iframe 
          title="VHservice Adresse"
          className="w-full h-full border-0"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2250.776077364805!2d12.502804577239634!3d55.658097498708144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4652538f9f0e156d%3A0xc38339d375a0045d!2sVigerslev%20All%C3%A9%20120%2C%202500%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1715600000000!5m2!1sda!2sdk"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
        <div className="absolute top-8 left-8 bg-white p-6 shadow-2xl border border-gray-100 hidden md:block pointer-events-none">
          <MapPin className="w-8 h-8 mb-2 text-black" />
          <p className="font-bold">{siteData.contact.address}</p>
          <p className="text-sm text-gray-500">Hovedkontor & Serviceområde</p>
        </div>
      </section>
    </main>
  );
}
